import { createContext, useContext, useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";

const MastersContext = createContext(null);
export const useMasters = () => useContext(MastersContext);

export function MastersProvider({ children }) {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const emptyVehicle = { vehicle_number: "", make_model: "", owner_name: "", vehicle_type: "", broker_id: "", broker_name: "", from_location: "", to_location: "", notes: "", owned: false };
  const [vForm, setVForm] = useState(emptyVehicle);
  const [dForm, setDForm] = useState({ name: "", phone: "", upi_id: "", license_number: "", aadhaar_number: "", notes: "" });
  const [cForm, setCForm] = useState({ name: "", address: "", gst_number: "", phone: "", email: "", notes: "", maps_url: "", field_labels: [], doc_titles: [] });
  const [err, setErr] = useState("");
  const [selCompany, setSelCompany] = useState("");
  const [departments, setDepartments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companyAddresses, setCompanyAddresses] = useState([]);
  const [deptForm, setDeptForm] = useState({ name: "", notes: "" });
  const emptyContact = { name: "", phone: "", email: "", designation: "", department_id: "", address: "", gst: "" };
  const [contactForm, setContactForm] = useState(emptyContact);
  const [editingContactId, setEditingContactId] = useState(null);
  const emptyAddress = { kind: "both", label: "", address: "", gst: "", department_id: "" };
  const [addressForm, setAddressForm] = useState(emptyAddress);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankForm, setBankForm] = useState({ bank_name: "", account_name: "", account_number: "", ifsc: "", notes: "" });
  const emptyBroker = { name: "", phone: "", upi_id: "", pan: "", gst: "", bank_name: "", account_name: "", account_number: "", ifsc: "", vehicle_number: "", vehicle_type: "", dimensions: "", weight_limit: "", driver_name: "", driver_phone: "", routes: [], notes: "" };
  const [brokers, setBrokers] = useState([]);
  const [bForm, setBForm] = useState(emptyBroker);
  const emptySub = { name: "", contact_person: "", phone: "", upi_id: "", email: "", address: "", gst: "", pan: "", hubs: [], notes: "" };
  const [subs, setSubs] = useState([]);
  const [sForm, setSForm] = useState(emptySub);
  const [dirQuery, setDirQuery] = useState({ from: "", to: "" });
  const [dirResults, setDirResults] = useState(null);
  const [editIds, setEditIds] = useState({});

  async function searchDirectory(e) {
    e.preventDefault();
    const r = await api.get("/brokers/directory", { params: { from_location: dirQuery.from, to_location: dirQuery.to } });
    setDirResults(r.data);
  }

  async function loadBanks() { const r = await api.get("/bank-accounts"); setBankAccounts(r.data); }
  useEffect(() => { loadBanks(); }, []);
  async function addBank(e) {
    e.preventDefault();
    try {
      await api.post("/bank-accounts", bankForm);
      setBankForm({ bank_name: "", account_name: "", account_number: "", ifsc: "", notes: "" });
      loadBanks();
    } catch (er) { setErr(formatApiErrorDetail(er?.response?.data?.detail) || "Could not add bank account"); }
  }
  async function delBank(id) { if (!confirm("Delete bank account?")) return; await api.delete(`/bank-accounts/${id}`); loadBanks(); }

  async function loadDeptContacts(companyId) {
    if (!companyId) { setDepartments([]); setContacts([]); setCompanyAddresses([]); return; }
    const { data } = await api.get(`/directory/company/${companyId}`);
    setDepartments(data.departments || []); setContacts(data.employees || []); setCompanyAddresses(data.addresses || []);
  }
  useEffect(() => { loadDeptContacts(selCompany); }, [selCompany]); // eslint-disable-line

  async function addDept(e) {
    e.preventDefault();
    await api.post("/departments", { company_id: selCompany, ...deptForm });
    setDeptForm({ name: "", notes: "" }); loadDeptContacts(selCompany);
  }
  async function delDept(id) { if (!confirm("Delete department?")) return; await api.delete(`/departments/${id}`); loadDeptContacts(selCompany); }
  async function addContact(e) {
    e.preventDefault();
    if (editingContactId) await api.put(`/contacts/${editingContactId}`, { company_id: selCompany, ...contactForm });
    else await api.post("/contacts", { company_id: selCompany, ...contactForm });
    setContactForm(emptyContact); setEditingContactId(null); loadDeptContacts(selCompany);
  }
  function editContact(ct) { setContactForm({ ...emptyContact, ...ct }); setEditingContactId(ct.id); }
  function cancelEditContact() { setContactForm(emptyContact); setEditingContactId(null); }
  async function delContact(id) { if (!confirm("Delete employee?")) return; await api.delete(`/contacts/${id}`); loadDeptContacts(selCompany); }
  async function addAddress(e) {
    e.preventDefault();
    await api.post("/company-addresses", { company_id: selCompany, ...addressForm });
    setAddressForm(emptyAddress); loadDeptContacts(selCompany);
  }
  async function delAddress(id) { if (!confirm("Delete address?")) return; await api.delete(`/company-addresses/${id}`); loadDeptContacts(selCompany); }
  async function bulkImportEmployees(text) {
    const { data } = await api.post("/contacts/bulk-import", { company_id: selCompany, text });
    await loadDeptContacts(selCompany);
    return data;
  }
  async function importEmployeesFile(file) {
    const fd = new FormData();
    fd.append("company_id", selCompany);
    fd.append("file", file);
    const { data } = await api.post("/contacts/import-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
    await loadDeptContacts(selCompany);
    return data;
  }
  async function importPreviewFile(file) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/contacts/import-preview-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.rows || [];
  }
  async function importMapped(rows, mapping, skip_first) {
    const { data } = await api.post("/contacts/import-mapped", { company_id: selCompany, rows, mapping, skip_first });
    await loadDeptContacts(selCompany);
    return data;
  }
  async function loadAutoFiled() { const { data } = await api.get("/directory/auto-filed"); return data; }
  async function confirmAutoFiled(kind, id) {
    const fd = new FormData(); fd.append("kind", kind); fd.append("id", id);
    await api.post("/directory/auto-filed/confirm", fd, { headers: { "Content-Type": "multipart/form-data" } });
  }
  async function deleteAutoFiled(kind, id) { await api.delete(`/directory/auto-filed/${kind}/${id}`); }

  const [locations, setLocations] = useState([]);
  const [people, setPeople] = useState([]);

  async function loadAll() {
    const [v, d, c, b, s] = await Promise.all([api.get("/vehicles"), api.get("/drivers"), api.get("/customers"), api.get("/brokers"), api.get("/sub-transporters")]);
    setVehicles(v.data); setDrivers(d.data); setCustomers(c.data); setBrokers(b.data); setSubs(s.data);
  }
  useEffect(() => { loadAll(); }, []);

  async function loadLookups() {
    const [l, p] = await Promise.all([api.get("/locations"), api.get("/people")]);
    setLocations(l.data); setPeople(p.data);
  }
  useEffect(() => { loadLookups(); }, []);
  async function addLocation(address) { await api.post("/locations", { address }); loadLookups(); }
  async function delLocation(id) { if (!confirm("Delete location?")) return; await api.delete(`/locations/${id}`); loadLookups(); }
  async function addPerson(body) { await api.post("/people", body); loadLookups(); }
  async function delPerson(id) { if (!confirm("Delete person?")) return; await api.delete(`/people/${id}`); loadLookups(); }
  async function scanDuplicates() { const { data } = await api.get("/masters/duplicates"); return data; }
  async function mergeMaster(type, keep_id, merge_ids) { await api.post("/masters/merge", { type, keep_id, merge_ids }); await loadAll(); await loadLookups(); }
  async function mergeSafeDupes() { const { data } = await api.post("/masters/merge-safe"); await loadAll(); await loadLookups(); return data; }
  async function autoMergeAll() { const { data } = await api.post("/masters/auto-merge-all"); await loadAll(); await loadLookups(); return data; }
  async function getAutoMergeSetting() { const { data } = await api.get("/masters/auto-merge-setting"); return !!data.enabled; }
  async function setAutoMergeSetting(enabled) { await api.put("/masters/auto-merge-setting", { enabled }); }

  // Keep companies / people / addresses de-duplicated automatically (on by default).
  const autoMergedRef = useRef(false);
  useEffect(() => {
    if (autoMergedRef.current) return;
    autoMergedRef.current = true;
    (async () => {
      try {
        const { data } = await api.get("/masters/auto-merge-setting");
        if (!data.enabled) return;
        const r = await api.post("/masters/auto-merge-all");
        if (r.data?.merged_records) { await loadAll(); await loadLookups(); }
      } catch (e) { /* ignore */ }
    })();
  }, []);

  async function saveMaster(coll, id, body, resetFn, editKey) {
    setErr("");
    try {
      if (id) await api.put(`/${coll}/${id}`, body); else await api.post(`/${coll}`, body);
      resetFn(); setEditIds((p) => ({ ...p, [editKey]: null })); loadAll();
    } catch (ex) { setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message); }
  }

  async function addBroker(e) { e.preventDefault(); await saveMaster("brokers", editIds.broker, bForm, () => setBForm(emptyBroker), "broker"); }
  function editBroker(b) { setBForm({ ...emptyBroker, ...b }); setEditIds((p) => ({ ...p, broker: b.id })); window.scrollTo({ top: 0, behavior: "smooth" }); }
  async function delBroker(id) { if (!confirm("Delete broker?")) return; await api.delete(`/brokers/${id}`); loadAll(); }

  async function addSub(e) { e.preventDefault(); await saveMaster("sub-transporters", editIds.sub, sForm, () => setSForm(emptySub), "sub"); }
  function editSub(s) { setSForm({ ...emptySub, ...s }); setEditIds((p) => ({ ...p, sub: s.id })); }
  async function delSub(id) { if (!confirm("Delete sub-transporter?")) return; await api.delete(`/sub-transporters/${id}`); loadAll(); }

  async function addVehicle(e) { e.preventDefault(); await saveMaster("vehicles", editIds.vehicle, vForm, () => setVForm(emptyVehicle), "vehicle"); }
  function editVehicle(v) { setVForm({ ...emptyVehicle, ...v }); setEditIds((p) => ({ ...p, vehicle: v.id })); window.scrollTo({ top: 0, behavior: "smooth" }); }

  async function addDriver(e) { e.preventDefault(); await saveMaster("drivers", editIds.driver, dForm, () => setDForm({ name: "", phone: "", upi_id: "", license_number: "", aadhaar_number: "", notes: "" }), "driver"); }
  function editDriver(d) { setDForm({ name: d.name || "", phone: d.phone || "", upi_id: d.upi_id || "", license_number: d.license_number || "", aadhaar_number: d.aadhaar_number || "", notes: d.notes || "" }); setEditIds((p) => ({ ...p, driver: d.id })); }

  async function addCustomer(e) {
    e.preventDefault();
    const body = {
      ...cForm,
      field_labels: (cForm.field_labels || []).map((s) => s.trim()).filter(Boolean),
      doc_titles: (cForm.doc_titles || []).map((s) => s.trim()).filter(Boolean),
    };
    await saveMaster("customers", editIds.customer, body, () => setCForm({ name: "", address: "", gst_number: "", phone: "", email: "", notes: "", maps_url: "", field_labels: [], doc_titles: [] }), "customer");
  }
  function editCustomer(c) { setCForm({ name: c.name || "", address: c.address || "", gst_number: c.gst_number || "", phone: c.phone || "", email: c.email || "", notes: c.notes || "", maps_url: c.maps_url || "", field_labels: c.field_labels || [], doc_titles: c.doc_titles || [] }); setEditIds((p) => ({ ...p, customer: c.id })); window.scrollTo({ top: 0, behavior: "smooth" }); }

  async function delVehicle(id) { if (!confirm("Delete vehicle?")) return; await api.delete(`/vehicles/${id}`); loadAll(); }
  async function delDriver(id) { if (!confirm("Delete driver?")) return; await api.delete(`/drivers/${id}`); loadAll(); }
  async function delCustomer(id) { if (!confirm("Delete customer?")) return; await api.delete(`/customers/${id}`); loadAll(); }

  const value = {
    vehicles, drivers, customers, vForm, setVForm, dForm, setDForm, cForm, setCForm, err,
    selCompany, setSelCompany, departments, contacts, companyAddresses, deptForm, setDeptForm,
    contactForm, setContactForm, editingContactId, addressForm, setAddressForm,
    bankAccounts, bankForm, setBankForm, brokers, bForm, setBForm, subs, sForm, setSForm,
    dirQuery, setDirQuery, dirResults, editIds, setEditIds, emptySub,
    searchDirectory, addBank, delBank, addDept, delDept, addContact, editContact, cancelEditContact, delContact,
    addAddress, delAddress, bulkImportEmployees, importEmployeesFile, importPreviewFile, importMapped,
    loadAutoFiled, confirmAutoFiled, deleteAutoFiled,
    addBroker, editBroker, delBroker, addSub, editSub, delSub,
    addVehicle, editVehicle, delVehicle, addDriver, editDriver, delDriver,
    addCustomer, editCustomer, delCustomer,
    locations, people, addLocation, delLocation, addPerson, delPerson,
    scanDuplicates, mergeMaster, mergeSafeDupes,
    autoMergeAll, getAutoMergeSetting, setAutoMergeSetting,
    loadAll,
  };
  return <MastersContext.Provider value={value}>{children}</MastersContext.Provider>;
}
